/**
 * FP3-119B — Frame Practice React Query client.
 *
 * Steady-state Admin workspace: previously visited pages render from
 * cache immediately; background refresh fires only when stale;
 * mutations invalidate ONLY affected keys.
 *
 * Defaults chosen to match the ticket's stale-window guidance:
 *   • Stable reference data (schools/providers/students directory,
 *     taxonomy) — 5–15 min stale, up to 30 min gc.
 *   • Operational summaries (progress, due-dates, reports) — 2–5 min.
 *   • Time-sensitive (today, notifications, sessions) — 15–60 s.
 *
 * Scope key discipline:
 *   Every query key includes the caller's user_id + organization_id
 *   + optional shadow_provider_id + as_org/as_school when relevant,
 *   so caches CANNOT be reused across users/orgs/shadow contexts.
 *   See `authScopeKey()` below and `resetCacheOnAuthChange()`.
 */
import { QueryClient } from "@tanstack/react-query";

// Named export so mutation code can import the same instance.
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Renders cached data immediately, revalidates in background.
      // NEVER blanks the page on route change.
      staleTime: 5 * 60 * 1000, // 5 min sensible default
      gcTime: 30 * 60 * 1000, // 30 min inactive cache
      refetchOnMount: false, // no refetch on remount if fresh
      refetchOnWindowFocus: false, // no thrash on tab focus
      refetchOnReconnect: "always", // refresh after net dropout
      retry: 1,
    },
    mutations: {
      retry: 0,
    },
  },
});

// FP3-119B — Stale-window map. Import in useQuery calls to apply the
// right window per surface. Comment lines document the volatility
// assumption behind each choice.
export const STALE = Object.freeze({
  // Stable / reference data — read-only for the session in practice.
  SCHOOLS: 15 * 60 * 1000,       // schools list rarely changes mid-session
  PROVIDERS: 10 * 60 * 1000,     // provider roster changes on admin action
  STUDENTS_DIRECTORY: 5 * 60 * 1000, // ~5 min freshness on 173-student list
  TAXONOMY: 60 * 60 * 1000,      // eligibility / domain configs

  // Operational summaries — used for oversight, tolerate brief lag.
  DASHBOARD: 2 * 60 * 1000,      // 2 min — school-wide snapshot (Today counts, health)
  PROGRESS: 3 * 60 * 1000,       // 3 min — session logs bump progress
  REPORTS: 3 * 60 * 1000,        // 3 min — same
  DUE_DATES: 5 * 60 * 1000,      // 5 min — dates rarely edit
  ASSIGNMENTS: 5 * 60 * 1000,    // 5 min
  COVERAGE: 3 * 60 * 1000,       // 3 min

  // Schedule data — time-sensitive but rarely edited mid-session.
  CALENDAR: 2 * 60 * 1000,       // 2 min — team/provider week/day views

  // Time-sensitive — session state changes throughout the day.
  TODAY: 60 * 1000,              // 1 min
  NOTIFICATIONS: 30 * 1000,      // 30 s
  FIDELITY: 2 * 60 * 1000,       // 2 min if enabled
});

/**
 * Build a scope suffix that goes into EVERY query key.
 * Prevents cross-user / cross-org / cross-shadow cache reuse.
 *
 * @param {object} auth - The value returned by `useAuth()`
 * @returns {object} scope object safe to embed in a query key
 */
export function authScopeKey(auth) {
  if (!auth) return { u: "", o: "" };
  return {
    u: auth.user?.id || "",
    o: auth.user?.organization_id || auth.organization?.id || "",
    // shadow_provider_id is set when a super_admin is inspecting
    // another provider's data — different shadow → separate cache.
    sp: auth.shadowProvider?.id || "",
  };
}

/**
 * Called from `auth.jsx::logout` and on organization / shadow changes.
 * Wipes ALL cached queries — a hard reset so the next authenticated
 * user cannot see the previous session's data.
 */
export function resetCacheOnAuthChange() {
  try {
    queryClient.clear();
  } catch {
    /* noop */
  }
}
